/** Helpers for Frappe /api/method responses + standalone client RPC. */

import { apiUrl, methodUrl as configMethodUrl, resolveSiteName } from './config'
import type { ApiError, AlternativeSlot } from './types/api'

export { apiUrl }
export function methodUrl(method: string): string {
  return configMethodUrl(method)
}

export function unwrap(input: unknown): unknown {
  if (input == null) return input
  if (typeof input !== 'object') return input
  const data = input as Record<string, any>

  // Normalized by frappeFetchPatch: { data, message }
  if (data.data != null && data.message != null) {
    return data.data
  }
  if (data.data != null && (data.success === true || data.message != null)) {
    return data.data
  }
  if (
    data.message != null &&
    typeof data.message === 'object' &&
    !Array.isArray(data.message)
  ) {
    if (
      'authenticated' in data.message ||
      'plan_code' in data.message ||
      data.message.docs ||
      data.message.boot
    ) {
      return data.message
    }
  }
  return data
}

export function siteName(bootSite?: string): string {
  return resolveSiteName(bootSite)
}

/**
 * Direct RPC that always returns Frappe `message`.
 */
function csrfHeaders(
  extra: Record<string, string> = {},
): Record<string, string> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'X-Frappe-Site-Name': siteName(),
    ...extra,
  }
  if (window.csrf_token && window.csrf_token !== '{{ csrf_token }}') {
    headers['X-Frappe-CSRF-Token'] = window.csrf_token
  }
  return headers
}

/**
 * Bound while this module first evaluates. `frappeFetchPatch` imports
 * `session`, which imports this file, before that patch replaces
 * `window.fetch`. Guest marketplace GETs must use this copy: the patched
 * fetch adds `Authorization: Bearer` and, on 401, refreshes the token and
 * sends the browser to `/login`. A stale Bearer is rejected with 401 before
 * `glamora.api.marketplace.*` runs.
 */
const guestFetch: typeof fetch = window.fetch.bind(window)

/**
 * GET RPC — CSRF is not checked on GET. Use for boot so a leftover Desk
 * `sid` cookie (same host, different port) does not block the first call.
 */
export async function rpcGet(
  method: string,
  args: Record<string, unknown> = {},
  options: { signal?: AbortSignal } = {},
): Promise<unknown> {
  const url = new URL(methodUrl(method), window.location.origin)
  for (const [k, v] of Object.entries(args || {})) {
    if (v === undefined || v === null || v === '') continue
    url.searchParams.set(k, String(v))
  }
  const res = await fetch(url.toString(), {
    method: 'GET',
    headers: csrfHeaders(),
    credentials: 'include',
    signal: options.signal,
  })
  return parseRpcResponse(res)
}

/**
 * Guest GET. No Authorization header, no token refresh, no redirect to
 * `/login` on 401/403 (the error is thrown for the page to show).
 */
export async function rpcGetGuest(
  method: string,
  args: Record<string, unknown> = {},
  options: { signal?: AbortSignal } = {},
): Promise<unknown> {
  const url = new URL(methodUrl(method), window.location.origin)
  for (const [k, v] of Object.entries(args || {})) {
    if (v === undefined || v === null || v === '') continue
    url.searchParams.set(k, String(v))
  }
  const res = await guestFetch(url.toString(), {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      'X-Frappe-Site-Name': siteName(),
    },
    credentials: 'omit',
    signal: options.signal,
  })
  return parseRpcResponse(res)
}

/**
 * Guest POST. Same rules as `rpcGetGuest`: no Authorization header, no token
 * refresh, no redirect to `/login`. A signed-in browser is still a guest for
 * this call, because credentials are omitted.
 */
export async function rpcPostGuest(
  method: string,
  args: Record<string, unknown> = {},
  options: { signal?: AbortSignal } = {},
): Promise<unknown> {
  const res = await guestFetch(methodUrl(method), {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json; charset=utf-8',
      'X-Frappe-Site-Name': siteName(),
    },
    credentials: 'omit',
    body: JSON.stringify(args || {}),
    signal: options.signal,
  })
  return parseRpcResponse(res)
}

/**
 * POST RPC. Many *reads* are implemented as POST (inventory levels/movements,
 * reports, availability), so `options.signal` exists to make them cancellable
 * the same way `rpcGet` is (`CACHE_AUDIT.md` C-08, `PLAN_TRACK_C.md` C1.2).
 */
export async function rpc(
  method: string,
  args: Record<string, unknown> = {},
  options: { signal?: AbortSignal } = {},
): Promise<unknown> {
  const res = await fetch(methodUrl(method), {
    method: 'POST',
    headers: csrfHeaders({ 'Content-Type': 'application/json; charset=utf-8' }),
    credentials: 'include',
    body: JSON.stringify(args || {}),
    signal: options.signal,
  })
  return parseRpcResponse(res)
}

async function parseRpcResponse(res: Response): Promise<unknown> {
  let body: any = null
  try {
    body = await res.json()
  } catch {
    body = null
  }

  if (!res.ok) {
    const csrfFail =
      body?.exc_type === 'CSRFTokenError' ||
      /csrf/i.test(String(body?.exc_type || ''))
    const err = new Error(
      csrfFail
        ? 'Session expired. Refresh the page and sign in again.'
        : messageFromBody(body) ||
            body?.exc_type ||
            `Request failed (${res.status})`,
    ) as ApiError
    err.status = res.status
    err.messages = []
    if (body?.code !== undefined && body?.code !== null) {
      err.code = String(body.code)
    }
    if (body?.exc_type) {
      err.exc_type = String(body.exc_type)
    }
    if (
      body?.details &&
      typeof body.details === 'object' &&
      !Array.isArray(body.details)
    ) {
      err.details = body.details as Record<string, unknown>
    }
    if (Array.isArray(body?.alternative_slots)) {
      err.alternative_slots = body.alternative_slots as AlternativeSlot[]
    }
    if (body?._server_messages) {
      try {
        err.messages = JSON.parse(body._server_messages).map((m: string) => {
          try {
            return JSON.parse(m).message
          } catch {
            return m
          }
        })
      } catch {
        /* ignore */
      }
    }
    if (
      body?.message &&
      typeof body.message === 'object' &&
      !Array.isArray(body.message)
    ) {
      err.data = body.message as Record<string, unknown>
    }
    if (body?.message && typeof body.message === 'string') {
      err.messages = (err.messages || []).concat(body.message)
    }
    if (err.messages?.length) {
      err.messages = err.messages
        .filter(Boolean)
        .map((m) => htmlToText(String(m)))
      err.message = err.messages.join(' ')
    }

    // Global hook: on HTTP 417 with code `terms_not_accepted`, refresh the
    // boot terms_status so the UI can react (blocking screen / grace banner).
    // We do this asynchronously and do not await it; the original error is
    // still thrown so callers keep their own messages.
    if (res.status === 417 && err.code === 'terms_not_accepted') {
      refreshTermsStatusOnBlock().catch(() => {
        /* ignore — the original error is what callers see */
      })
    }

    throw err
  }

  // A 2xx that is not JSON (a proxy or login page, an empty body) is not an
  // answer from the API: treat it as a failure instead of "success, no data".
  if (body === null && res.status !== 204) {
    throw new Error(
      'The server sent an unreadable response. Check your connection and try again.',
    )
  }

  if (body && typeof body === 'object') {
    // Glamora success_response puts payload in `data` and a status string in `message`.
    // Classic Frappe methods put the real return value in `message` only.
    if (body.data !== undefined && body.data !== null) {
      if (body.message === undefined || typeof body.message === 'string') {
        return body.data
      }
    }
    if (body.message !== undefined) return body.message
    if (body.data !== undefined) return body.data
  }
  return body
}

export function formatMoney(n: unknown, currency = 'KES'): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      maximumFractionDigits: 2,
    }).format(Number(n || 0))
  } catch {
    return String(n ?? 0)
  }
}

/**
 * Deprecated aliases. `src/datetime.ts` is the app's date/time entry point
 * (Phase 2 item 6); these stay so the existing call sites keep working.
 *
 * @deprecated Import `formatDate` / `formatTime` from `src/datetime.ts`.
 */
export { formatDate, formatTime } from './datetime'

export function companyFilter(
  session: { boot?: { company?: string } },
  extra: Record<string, unknown> = {},
): Record<string, unknown> {
  const filters = { ...extra }
  if (session.boot?.company) filters.company = session.boot.company
  return filters
}

// ── Server failures → one Error ─────────────────────────────────────────────
//
// Every failed server request must surface as a thrown Error whose message is
// the backend's own text, so a caller can only reach its success branch after
// a real success. `rpc` / `rpcGet` already throw. frappe-ui's `useCall`,
// `useList().insert/setValue/delete` and `useDoc().setValue/delete` do NOT:
// `submit()` resolves `null` and parks the failure on `call.error`. Run those
// through `submitDoc` below, never `await call.submit()` bare.

const MARKUP = /<\/?[a-z][^>]*>|&(?:#\d+|#x[\da-f]+|[a-z]+);/i

/**
 * Plain text for a message Frappe wrapped in HTML (`<strong>`, `<br>`, lists,
 * entities). Block ends become spaces; the wording is otherwise untouched.
 */
export function htmlToText(input: string): string {
  if (!MARKUP.test(input)) return input.trim()
  const spaced = input.replace(/<\/(li|p|div|tr)>|<br\s*\/?>/gi, ' ')
  if (typeof DOMParser !== 'undefined') {
    const doc = new DOMParser().parseFromString(spaced, 'text/html')
    return (doc.body.textContent ?? spaced).replace(/\s+/g, ' ').trim()
  }
  return spaced
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, '&')
    .replace(/\s+/g, ' ')
    .trim()
}

/** `frappe.exceptions.ValidationError: text` → `text`. */
function withoutExceptionPath(line: string): string {
  return line.replace(/^(?:[A-Za-z_]\w*\.)+[A-Za-z_]\w*:\s*/, '').trim()
}

function stringsOf(list: unknown[]): string[] {
  const out: string[] = []
  for (const item of list) {
    let value: unknown = item
    if (typeof item === 'string') {
      try {
        value = JSON.parse(item)
      } catch {
        value = item
      }
    }
    const text =
      typeof value === 'string'
        ? value
        : value && typeof value === 'object'
          ? (value as { message?: unknown }).message
          : null
    if (typeof text === 'string' && text.trim()) out.push(text)
  }
  return out
}

/**
 * The backend's text for a refused request, from whichever body shape it
 * used: `{ errors: [{ message }] }` (document API), `_error_message`,
 * `_server_messages` (JSON list of JSON strings), `{ message }`, `exception`
 * and last of all the final line of `exc`. Tracebacks are never returned.
 * HTML is converted to text. `null` when the body carries no text.
 */
export function messageFromBody(body: unknown): string | null {
  if (typeof body === 'string') return htmlToText(body) || null
  if (!body || typeof body !== 'object') return null
  const b = body as Record<string, unknown>
  const found: string[] = []

  if (Array.isArray(b.errors)) found.push(...stringsOf(b.errors))
  if (!found.length && typeof b._error_message === 'string') {
    found.push(b._error_message)
  }
  if (!found.length && typeof b._server_messages === 'string') {
    try {
      const parsed: unknown = JSON.parse(b._server_messages)
      if (Array.isArray(parsed)) found.push(...stringsOf(parsed))
    } catch {
      /* not JSON: fall through to the other shapes */
    }
  }
  if (!found.length && typeof b.message === 'string' && b.message.trim()) {
    found.push(b.message)
  }
  if (!found.length && Array.isArray(b.message)) {
    found.push(...stringsOf(b.message))
  }
  if (!found.length && typeof b.exception === 'string') {
    const text = withoutExceptionPath(b.exception.split('\n')[0] ?? '')
    if (text) found.push(text)
  }
  if (!found.length && typeof b.exc === 'string') {
    try {
      const traces: unknown = JSON.parse(b.exc)
      const last = Array.isArray(traces) ? traces[traces.length - 1] : null
      const lines =
        typeof last === 'string' ? last.split('\n').filter((l) => l.trim()) : []
      const text = withoutExceptionPath(lines[lines.length - 1] ?? '')
      if (text) found.push(text.slice(0, 300))
    } catch {
      /* unreadable: no text */
    }
  }

  const text = htmlToText(found.join(' '))
  return text || null
}

// ── Auth failure or transient failure? ──────────────────────────────────────
//
// `rpc` / `rpcGet` throw an Error with `status` set for every HTTP refusal
// (`parseRpcResponse`); a request that never got an answer (offline, DNS, CORS,
// a dropped connection, our own timeout) rejects from `fetch` with a
// TypeError / AbortError that has no `status`; a 2xx that is not a usable
// answer throws a plain Error without one. Only an explicit 401 / 403 (or an
// AuthenticationError type) proves the session is gone. Everything else could
// be a hiccup, so it must never be read as "signed out".

export type FailureKind = 'auth' | 'transient'

export function failureKind(e: unknown): FailureKind {
  if (!e || typeof e !== 'object') return 'transient'
  const o = e as { status?: unknown; type?: unknown; exc_type?: unknown }
  if (o.status === 401 || o.status === 403) return 'auth'
  if (
    o.type === 'AuthenticationError' ||
    o.exc_type === 'AuthenticationError'
  ) {
    return 'auth'
  }
  return 'transient'
}

const BARE_TYPE_TEXT: Record<string, string> = {
  PermissionError: 'You do not have permission to do this.',
  AuthenticationError: 'Your session has expired. Sign in again.',
  DoesNotExistError: 'That record was not found.',
}

/**
 * A failure as plain text for the user: the backend's wording verbatim, HTML
 * converted to text, and the `Type: ` prefix frappe-ui puts in front of
 * document-API errors removed.
 */
export function errorMessage(e: unknown): string {
  if (!e) return 'Something went wrong'
  let text = ''
  let type = ''
  if (typeof e === 'string') {
    text = e
  } else if (typeof e === 'object') {
    const o = e as Record<string, unknown>
    if (typeof o.type === 'string') type = o.type
    if (Array.isArray(o.messages) && o.messages.length) {
      text = o.messages.filter(Boolean).join(' ')
    } else if (typeof o.message === 'string' && o.message.trim()) {
      text = o.message
    } else {
      text = messageFromBody(o) ?? ''
    }
  }
  text = htmlToText(text)
  if (type && text.startsWith(`${type}: `)) {
    text = text.slice(type.length + 2).trim()
  } else if (type && text === type) {
    text = BARE_TYPE_TEXT[type] ?? 'The server refused the request.'
  }
  return text || 'Something went wrong'
}

/** Alias kept for the dialogs that name it for what it is. */
export const serverErrorText = errorMessage

/**
 * The shape of a frappe-ui `useCall` (also `useList().insert/setValue/delete`
 * and `useDoc().setValue/delete`): `submit` resolves `null` on a refusal and
 * the failure is left on `error`.
 */
interface MutationCall<T, P> {
  submit: (params?: P) => Promise<T | null | undefined>
  error?: unknown
}

/**
 * Run a frappe-ui mutation and throw when it failed.
 *
 * - Success: resolves with the data the server returned.
 * - Refusal (HTTP error, validation, permission, network): throws an `Error`
 *   whose `message` is the backend's text (see `errorMessage`); the original
 *   failure is on `cause`, and `type` is copied when frappe-ui provided one.
 * - A 2xx with no data also throws, because nothing says the write happened.
 *   Pass `{ allowEmpty: true }` only for a call documented to return nothing.
 *
 * Callers toast, close a dialog or navigate only after this resolves, and
 * show the thrown message inline (dialogs) or with `toast.error` (elsewhere).
 */
export async function submitDoc<T, P = Record<string, unknown>>(
  call: MutationCall<T, P>,
  payload?: P,
  options: { allowEmpty?: boolean; emptyMessage?: string } = {},
): Promise<T> {
  let out: T | null | undefined
  try {
    out = await call.submit(payload)
  } catch (e) {
    throw failureError(e)
  }
  if (call.error) throw failureError(call.error)
  if (out === null || out === undefined) {
    if (options.allowEmpty) return out as T
    throw new Error(
      options.emptyMessage ??
        'The server did not confirm the change. Nothing was saved.',
    )
  }
  return out
}

function failureError(failure: unknown): Error {
  const err = new Error(errorMessage(failure))
  ;(err as Error & { cause?: unknown }).cause = failure
  if (failure && typeof failure === 'object') {
    const o = failure as { type?: unknown; status?: unknown }
    if (typeof o.type === 'string')
      (err as Error & { type?: string }).type = o.type
    if (typeof o.status === 'number') {
      ;(err as Error & { status?: number }).status = o.status
    }
  }
  return err
}

/**
 * Throw unless an `rpc` answer actually carries the payload the caller is
 * about to treat as success (a stored record, a token set, an invoice).
 */
export function requireData<T>(value: T | null | undefined, what: string): T {
  if (value === null || value === undefined || value === ('' as unknown)) {
    throw new Error(`The server did not confirm ${what}.`)
  }
  return value
}

/**
 * `requireData` for an object answer: throws unless `value[key]` is present.
 * Used by the `rpc` wrappers so a 2xx that does not carry the record (the
 * appointment, the invoice, the leave request) is never reported as done.
 */
export function requireField<T extends object, K extends keyof T>(
  value: T | null | undefined,
  key: K,
  what: string,
): T {
  if (
    !value ||
    typeof value !== 'object' ||
    value[key] === null ||
    value[key] === undefined ||
    value[key] === ('' as unknown)
  ) {
    throw new Error(`The server did not confirm ${what}.`)
  }
  return value
}

/**
 * Await an `rpc` answer and refuse one that lacks the payload the wrapper's
 * callers depend on. With `key`, `answer[key]` must be present; without it the
 * answer itself must be non-empty.
 */
export async function verified<T extends object>(
  answer: Promise<unknown>,
  what: string,
  key?: keyof T,
): Promise<T> {
  const value = (await answer) as T | null | undefined
  return key === undefined
    ? requireData(value, what)
    : requireField(value, key, what)
}

/** `verified` for an answer that must be a list. */
export async function verifiedList<T>(
  answer: Promise<unknown>,
  what: string,
): Promise<T[]> {
  const value = await answer
  if (!Array.isArray(value)) {
    throw new Error(`The server did not confirm ${what}.`)
  }
  return value as T[]
}

/**
 * Refresh the session boot's terms_status when a `terms_not_accepted` error
 * is encountered. This is called from the global error hook in `parseRpcResponse`.
 * It uses the existing `fetchBoot` from session.ts to re-read the boot data.
 * We import dynamically to avoid a circular dependency (session.ts imports api.ts).
 */
let refreshTermsStatusOnBlock: () => Promise<void> = async () => {
  /* placeholder — replaced by session.ts on load */
}

export function __setRefreshTermsStatusHook(fn: () => Promise<void>): void {
  refreshTermsStatusOnBlock = fn
}
